import { Consumer } from 'models/consumer';
import { Insurance } from 'models/insurance';
import { CacheTypes } from 'modules/types/mobileCaching';
import { Alert } from 'react-native';
import analyticsService, { AnalyticsEvent } from 'services/AnalyticsService';
import { ProviderDetail } from 'store/findProvider/types';
import * as geoLocationSelectors from 'store/geolocation/selectors';
import {
  currentConsumerPcpDoctorsSelector,
  myDoctorRequestStateSelector
} from 'store/myDoctors/selectors';
import { RootDispatch, RootState } from 'store/types';
import { mapPreferredProviderDto } from './mappers';

export const GET_MY_DOCTORS_CACHE_SUCCESS = 'MY_DOCTORS.GET_MY_DOCTORS_CACHE_SUCCESS';

export const GET_MY_DOCTORS = 'MY_DOCTORS.GET_MY_DOCTORS';
export const GET_MY_DOCTORS_SUCCESS = 'MY_DOCTORS.GET_MY_DOCTORS_SUCCESS';
export const GET_MY_DOCTORS_FAIL = 'MY_DOCTORS.GET_MY_DOCTORS_FAIL';

export const getMyDoctors = (
  skipCache?: boolean,
  useRequestState = true,
  options = {},
  params = {}
) => (dispatch: Function, getState: Function) => {
  const state = getState();
  const geoLocationCoords = geoLocationSelectors.geolocationCoordsSelector(state);
  let location = {};

  if (geoLocationCoords?.latitude && geoLocationCoords?.longitude) {
    location = {
      lat: geoLocationCoords.latitude,
      lng: geoLocationCoords.longitude
    };
  }

  return dispatch({
    type: GET_MY_DOCTORS,
    payload: {
      useRequestState,
      requestStateSelector: myDoctorRequestStateSelector,
      cacheType: CacheTypes.MY_DOCTORS,
      cacheRetrievalSuccessAction: GET_MY_DOCTORS_CACHE_SUCCESS,
      client: 'myDoctors',
      request: {
        method: 'POST',
        url: 'mydoctors',
        data: {
          location
        },
        params: {
          skipCache,
          retrieveGrantorsData: true,
          ...params
        },
        ...options
      }
    }
  });
};

export const UPDATE_PCP_PROVIDER = 'PROVIDERS.UPDATE_PCP_PROVIDER';
export const UPDATE_PCP_PROVIDER_FAIL = 'PROVIDERS.UPDATE_PCP_PROVIDER_FAIL';
export const UPDATE_PCP_PROVIDER_SUCCESS = 'PROVIDERS.UPDATE_PCP_PROVIDER_SUCCESS';
export const updatePcpProviderRequest = (
  subscriberId: string,
  suffix: string,
  corpProvId: string
) => ({
  type: UPDATE_PCP_PROVIDER,
  payload: {
    client: 'myDoctors',
    request: {
      method: 'POST',
      url: '/selecthealth/pcp',
      data: {
        subscriberId,
        suffix,
        providerId: corpProvId
      }
    }
  }
});

export const updatePcpProviderAndRefreshMyDoctors = (
  subscriberId: string,
  suffix: string,
  corpProvId: string
) => async (dispatch: Function) => {
  const updateRes = await dispatch(updatePcpProviderRequest(subscriberId, suffix, corpProvId));

  if (updateRes.error) {
    return updateRes;
  }

  return dispatch(getMyDoctors(true, false));
};

/** Thunk for updating pcp provider */
export const maybeUpdatePcpProvider = (
  provider: ProviderDetail,
  consumer: Consumer,
  insurances: Insurance[]
) => (dispatch: Function) => {
  const facetsInsurance = insurances.find(insurance => insurance.source === 'Facets');

  if (!facetsInsurance) {
    return Alert.alert('Unable to set as PCP', 'Unable to find a valid insurance');
  }

  const insuranceMember = facetsInsurance.members.find(
    member => member.consumerId === consumer.consumerId
  );

  if (!insuranceMember) {
    return Alert.alert('Unable to set as PCP', 'Unable to find a valid insurance');
  }

  const { subscriberId, suffix } = insuranceMember;
  const { corpProvId } = provider;

  if (!subscriberId || !suffix || !corpProvId) {
    return Alert.alert('Unable to set as PCP', 'Information could not be verified');
  }

  return dispatch(updatePcpProviderRequest(subscriberId, suffix, corpProvId));
};

export const UPDATE_PREFERRED_DOCTORS = 'PROFILE.UPDATE_PREFERRED_DOCTORS';
export const UPDATE_PREFERRED_DOCTORS_SUCCESS = 'PROFILE.UPDATE_PREFERRED_DOCTORS_SUCCESS';
export const UPDATE_PREFERRED_DOCTORS_FAIL = 'PROFILE.UPDATE_PREFERRED_DOCTORS_FAIL';
export const updatePreferredDoctors = (consumerId: string, providerId: string, favor: boolean) => ({
  type: UPDATE_PREFERRED_DOCTORS,
  payload: {
    client: 'preference',
    request: {
      headers: { consumerid: consumerId }, // Make sure to put consumerid in header
      method: favor ? 'post' : 'delete',
      url: '/preferredproviders',
      data: mapPreferredProviderDto(consumerId, providerId)
    }
  }
});

export const updatePreferredDoctorsAndRefreshMyDoctors = (
  consumerId: string,
  providerId: string,
  favor: boolean
) => async (dispatch: Function) => {
  const updateResponse = await dispatch(updatePreferredDoctors(consumerId, providerId, favor));

  if (updateResponse && updateResponse.error) {
    return updateResponse;
  }

  return dispatch(getMyDoctors(true, false));
};

export const getCurrentSignedInUserPrimaryDoctor = () => async (
  dispatch: RootDispatch,
  getState: () => RootState
) => {
  const state = getState();
  const primaryDoctor = currentConsumerPcpDoctorsSelector(state)[0]?.displayName;

  const attribute = 'name_of_PCP';
  const value = primaryDoctor || 'No PCP';
  analyticsService.setUserAttribute(attribute, value);
  return dispatch({ type: 'GET_CURRENT_SIGNED_IN_USER_PRIMARY_DOCTOR' });
};
